import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useCare } from '../contexts/CareContext'
import { useToast } from '../components/shared/Toast'
import LoadingSpinner from '../components/shared/LoadingSpinner'
import EmptyState from '../components/shared/EmptyState'
import AppointmentForm from '../components/appointments/AppointmentForm'
import { generateAppointmentPDF } from '../components/reports/generateAppointmentPDF'

const todayISO = () => new Date().toLocaleDateString('en-CA')
const fmtDate = (iso) => new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })

function AppointmentCard({ appt, canManage, recipient, onEdit, toast }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <button className="card-tap" onClick={() => setOpen((o) => !o)} style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: 16 }}>
        <div className="spread">
          <div>
            <div style={{ fontWeight: 700 }}>{appt.doctor_name}</div>
            <div className="muted" style={{ fontSize: 14 }}>{appt.specialty || appt.purpose || ''}</div>
          </div>
          <div className="center">
            <div style={{ fontWeight: 700, fontSize: 14 }}>{fmtDate(appt.appointment_date)}</div>
            {appt.appointment_time && <div className="muted" style={{ fontSize: 12 }}>{appt.appointment_time.slice(0, 5)}</div>}
          </div>
        </div>
      </button>
      {open && (
        <div style={{ padding: '0 16px 16px', borderTop: '1px solid var(--border)' }} className="stack">
          <div className="mt-16" />
          {appt.location && <Detail label="Location" value={appt.location} />}
          {appt.purpose && <Detail label="Purpose" value={appt.purpose} />}
          {appt.pre_appointment_notes && <Detail label="Pre-visit notes" value={appt.pre_appointment_notes} />}
          {appt.post_appointment_notes && <Detail label="After the visit" value={appt.post_appointment_notes} />}
          {appt.action_items && <Detail label="Action items" value={appt.action_items} />}
          <div className="row" style={{ gap: 10, marginTop: 4 }}>
            {canManage && <button className="btn btn-sm btn-secondary" onClick={() => onEdit(appt)}>{appt.post_appointment_notes ? 'Edit' : 'Add post-visit notes'}</button>}
            <button className="btn btn-sm btn-ghost" onClick={() => { try { generateAppointmentPDF(recipient, appt) } catch (e) { toast.error('Could not create PDF.') } }}>Export PDF</button>
          </div>
        </div>
      )}
    </div>
  )
}
function Detail({ label, value }) {
  return (
    <div>
      <div className="section-title">{label}</div>
      <div style={{ fontSize: 15, marginTop: 2, whiteSpace: 'pre-wrap' }}>{value}</div>
    </div>
  )
}

export default function AppointmentsPage() {
  const { recipient, role } = useCare()
  const toast = useToast()
  const navigate = useNavigate()
  const canManage = role === 'admin' || role === 'family_member'

  const [appts, setAppts] = useState([])
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState(null)
  const [showPast, setShowPast] = useState(false)
  const today = todayISO()

  const load = useCallback(async () => {
    if (!recipient) return
    const { data } = await supabase
      .from('appointments')
      .select('*')
      .eq('care_recipient_id', recipient.id)
      .order('appointment_date', { ascending: true })
    setAppts(data || [])
    setLoading(false)
  }, [recipient])

  useEffect(() => { load() }, [load])

  if (!recipient || loading) return <LoadingSpinner label="Loading appointments…" />

  const upcoming = appts.filter((a) => a.appointment_date >= today)
  const past = appts.filter((a) => a.appointment_date < today).reverse()

  return (
    <div className="stack">
      <button onClick={() => navigate('/more')} style={{ background: 'none', border: 'none', color: 'var(--teal)', fontWeight: 600, alignSelf: 'flex-start', padding: 0 }}>
        ← More</button>

      <div className="spread">
        <h1 className="title">Appointments</h1>
        {canManage && <button className="btn btn-sm" onClick={() => setForm({})}>+ Add</button>}
      </div>

      {appts.length === 0 ? (
        <EmptyState icon="📅" title="No appointments yet" message={canManage ? 'Add the first appointment to track visits.' : 'An admin or family member can add appointments.'} action={canManage && <button className="btn" onClick={() => setForm({})}>+ Add appointment</button>} />
      ) : (
        <>
          <div className="section-title">Upcoming</div>
          {upcoming.length === 0 ? (
            <div className="card muted" style={{ fontSize: 14 }}>No upcoming appointments.</div>
          ) : (
            <div className="stack" style={{ gap: 10 }}>
              {upcoming.map((a) => <AppointmentCard key={a.id} appt={a} canManage={canManage} recipient={recipient} onEdit={(x) => setForm({ appointment: x })} toast={toast} />)}
            </div>
          )}

          {past.length > 0 && (
            <>
              <button onClick={() => setShowPast((s) => !s)} className="spread" style={{ background: 'none', border: 'none', padding: '8px 0', width: '100%' }}>
                <span className="section-title">Past ({past.length})</span>
                <span className="muted">{showPast ? '▲' : '▼'}</span>
              </button>
              {showPast && (
                <div className="stack" style={{ gap: 10 }}>
                  {past.map((a) => <AppointmentCard key={a.id} appt={a} canManage={canManage} recipient={recipient} onEdit={(x) => setForm({ appointment: x })} toast={toast} />)}
                </div>
              )}
            </>
          )}
        </>
      )}

      {form && (
        <AppointmentForm
          recipientId={recipient.id}
          appointment={form.appointment}
          onCancel={() => setForm(null)}
          onDone={() => { setForm(null); load() }}
        />
      )}

      <div style={{ height: 8 }} />
    </div>
  )
}
